import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  ChartBarIcon, 
  CogIcon, 
  BuildingOfficeIcon, 
  PresentationChartLineIcon,
  ArrowRightIcon 
} from '@heroicons/react/24/outline';

const ServicesSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const services = [
    {
      icon: ChartBarIcon,
      title: "Token Liquidity Health Check",
      description: "A comprehensive audit of your token's liquidity, market structure, and tokenomics. We assess depth, spread, slippage, and overall market health to identify optimization opportunities.",
      features: ["Liquidity depth analysis", "Market structure review", "Tokenomics assessment", "Benchmark comparison"],
      image: "https://images.unsplash.com/photo-1639815188546-c43c240ff4df"
    },
    {
      icon: CogIcon,
      title: "Market Maker Evaluation",
      description: "We assess and benchmark your existing or proposed market maker(s), structuring optimal MM strategies and term sheets to maximize liquidity efficiency.",
      features: ["Performance benchmarking", "Strategy optimization", "Term sheet structuring", "ROI analysis"],
      image: "https://images.pexels.com/photos/5475750/pexels-photo-5475750.jpeg"
    },
    {
      icon: BuildingOfficeIcon,
      title: "Exchange Strategy",
      description: "From listing strategy to exchange tiering and coverage planning, we help optimize your token's venue exposure and trading accessibility.",
      features: ["Listing strategy", "Exchange tiering", "Coverage planning", "Fee optimization"],
      image: "https://images.unsplash.com/photo-1488590528505-98d2b5aba04b"
    },
    {
      icon: PresentationChartLineIcon,
      title: "Monitoring & Reporting",
      description: "Our analytics platform provides real-time scorecards, benchmarking, and scenario modeling to track your liquidity health and performance metrics.",
      features: ["Real-time monitoring", "Performance scorecards", "Scenario modeling", "Custom reporting"],
      image: "https://images.unsplash.com/photo-1573497701175-00c200fd57f0"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <section id="services" className="py-24 bg-gradient-to-b from-primary-900 to-primary-800">
      <div className="section-padding">
        <div className="container-width">
          {/* Section Header */}
          <motion.div
            ref={ref}
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <div className="inline-block px-6 py-2 glass-card text-accent-gold text-sm font-medium tracking-wide uppercase mb-6">
              Our Core Services
            </div>
            <h2 className="text-display font-serif font-bold text-cream-100 mb-6">
              Strategic Advisory That{' '}
              <span className="text-gradient">Delivers Results</span>
            </h2>
            <p className="text-body-lg text-cream-200 max-w-3xl mx-auto">
              We provide comprehensive consulting services that bridge traditional finance 
              expertise with cutting-edge blockchain technology, delivering measurable 
              improvements to your token's liquidity performance.
            </p>
          </motion.div>

          {/* Services Grid */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            className="grid grid-cols-1 lg:grid-cols-2 gap-8"
          >
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                variants={itemVariants}
                className="group glass-card glass-card-hover p-8 relative overflow-hidden"
              >
                {/* Background Image */}
                <div className="absolute inset-0 opacity-5 group-hover:opacity-10 transition-opacity duration-300">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Content */}
                <div className="relative z-10">
                  {/* Icon */}
                  <div className="mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-accent-blue to-accent-gold rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <service.icon className="h-8 w-8 text-white" />
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="text-heading-md font-serif font-bold text-cream-100 mb-4 group-hover:text-white transition-colors duration-300">
                    {service.title}
                  </h3>

                  {/* Description */}
                  <p className="text-cream-200 mb-6 leading-relaxed">
                    {service.description}
                  </p>

                  {/* Features */}
                  <ul className="space-y-2 mb-6">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-sm text-cream-300">
                        <div className="w-1.5 h-1.5 bg-accent-gold rounded-full mr-3" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  {/* Learn More Link */}
                  <div className="flex items-center text-accent-blue group-hover:text-accent-gold transition-colors duration-300 cursor-pointer">
                    <span className="text-sm font-medium mr-2">Learn More</span>
                    <ArrowRightIcon className="h-4 w-4 group-hover:translate-x-1 transition-transform duration-300" />
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* CTA Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="text-center mt-20"
          >
            <div className="glass-card p-12 max-w-4xl mx-auto">
              <h3 className="text-heading-lg font-serif font-bold text-cream-100 mb-4">
                Ready to Optimize Your Token's Liquidity?
              </h3>
              <p className="text-body-lg text-cream-200 mb-8 max-w-2xl mx-auto">
                Get started with a comprehensive liquidity assessment and discover 
                how our strategic advisory can enhance your token's market performance.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button className="btn-primary">
                  Schedule Consultation
                </button>
                <button className="btn-secondary">
                  Download Case Study
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;